import { CommonModule } from '@angular/common'
import { HTTP_INTERCEPTORS } from '@angular/common/http'
import { APP_INITIALIZER, NgModule } from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { RouterModule } from '@angular/router'
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap'
import { EffectsModule } from '@ngrx/effects'
import { Store, StoreModule } from '@ngrx/store'
import { CartPageLayoutHandler } from '@spartacus/cart/base/components'
import { CartBaseCoreModule } from '@spartacus/cart/base/core'
import { OccCartAdapter } from '@spartacus/cart/base/occ'
import { ActiveCartFacade, CartBaseRootModule, MultiCartFacade } from '@spartacus/cart/base/root'
import {
  CmsConfig,
  ConfigurationService,
  I18nModule,
  provideConfig,
  UrlModule,
  WindowRef
} from '@spartacus/core'
import {
  DIALOG_TYPE,
  FormErrorsModule,
  GlobalMessageComponentModule,
  IconModule,
  ItemCounterModule,
  KeyboardFocusModule,
  LayoutConfig,
  MediaModule,
  OutletModule
} from '@spartacus/storefront'
import { NgxFileDropModule } from 'ngx-file-drop'
import { EbDatePickerModule } from '../../components/date-picker/eb-date-picker.module'
import { EbInfoBoxModule } from '../../components/info-box/eb-info-box.module'
import { EbModalComponent } from '../../components/modal/eb-modal.component'
import { EbModalModule } from '../../components/modal/eb-modal.module'
import { EbSpinnerModule } from '../../components/spinner/eb-spinner.module'
import { EbSuccessorModule } from '../../components/successor/eb-successor.module'
import { EbAuthGuard } from '../auth/guards/eb-auth.guard'
import { EbFormModule } from '../form/eb-form.module'
import { EbProductAvailabilityModule } from '../product/components/product-availability/eb-product-availability.module'
import { EbAddToCartComponent } from './components/add-to-cart/eb-add-to-cart.component'
import { EbAddedToCartDialogComponent } from './components/added-to-cart-dialog/eb-added-to-cart-dialog.component'
import { EbCartDetailsComponent } from './components/cart-details/eb-cart-details.component'
import { EbCartItemComponent } from './components/cart-item/eb-cart-item.component'
import { EbCartItemHeaderComponent } from './components/cart-item-header/eb-cart-item-header.component'
import { EbCartItemListComponent } from './components/cart-item-list/eb-cart-item-list.component'
import { EbCartTotalsComponent } from './components/cart-totals/eb-cart-totals.component'
import {EbItemCounterModule} from "./components/item-counter/eb-item-counter.module";
import { EbOrderSummaryComponent } from './components/order-summary/eb-order-summary.component'
import { EbProductSalesTextComponent } from './components/product-sales-text/eb-product-sales-text.component'
import { EbPurchaseProductComponent } from './components/purchase-product/eb-purchase-product.component'
import { EbQuickOrderFormComponent } from './components/quick-order/eb-quick-order-form.component'
import { EbQuickOrderComponent } from './components/quick-order/eb-quick-order.component'
import { EbCartPageLayoutHandler } from './eb-cart-page-layout-handler'
import { EbCartInterceptor } from './interceptor/eb-cart.interceptor'
import { STORE_FEATURE_EB_MULTI_CART } from './interface/eb-multi-cart-state.interface'
import { EbCartAdapter } from './services/connectors/eb-cart.adapter'
import { EbActiveCartService } from './services/eb-active-cart.service'
import { EbMultiCartService } from './services/eb-multi-cart.service'
import { EbQuickOrderService } from './services/eb-quick-order.service'
import { EbCartEntryEffects } from './store/effects/eb-cart-entry.effect'
import { EbCartEffects } from './store/effects/eb-cart.effect'
import { ebCartModificationsReducer } from './store/reducers/eb-multi-cart.reducer'
export const defaultCartDialogModalLayoutConfig: LayoutConfig = {
  launch: {
    ADDED_TO_CART: {
      inlineRoot: true,
      component: EbAddedToCartDialogComponent,
      dialogType: DIALOG_TYPE.DIALOG
    },
    FILE_DROP_CHANGE: {
      inlineRoot: true,
      component: EbModalComponent,
      dialogType: DIALOG_TYPE.POPOVER_CENTER_BACKDROP
    }
  }
}

@NgModule({
  imports: [
    CommonModule,
    I18nModule,
    ReactiveFormsModule,
    ItemCounterModule,
    UrlModule,
    CartBaseRootModule,
    CartBaseCoreModule,
    MediaModule,
    OutletModule,
    NgbTooltipModule,
    IconModule,
    EbSpinnerModule,
    EbDatePickerModule,
    EbInfoBoxModule,
    StoreModule.forFeature(STORE_FEATURE_EB_MULTI_CART, ebCartModificationsReducer),
    EffectsModule.forFeature([EbCartEffects, EbCartEntryEffects]),
    GlobalMessageComponentModule,
    EbProductAvailabilityModule,
    EbSuccessorModule,
    FormErrorsModule,
    EbFormModule,
    EbModalModule,
    RouterModule,
    NgxFileDropModule,
    KeyboardFocusModule,
    EbItemCounterModule
  ],
  declarations: [
    EbAddToCartComponent,
    EbAddedToCartDialogComponent,
    EbCartItemComponent,
    EbCartItemHeaderComponent,
    EbCartDetailsComponent,
    EbCartItemListComponent,
    EbOrderSummaryComponent,
    EbCartTotalsComponent,
    EbPurchaseProductComponent,
    EbQuickOrderFormComponent,
    EbQuickOrderComponent,
    EbProductSalesTextComponent
  ],
  providers: [
    provideConfig(defaultCartDialogModalLayoutConfig),
    provideConfig({
      cmsComponents: {
        QuickOrderComponent: {
          component: EbQuickOrderComponent,
          guards: [EbAuthGuard]
        },
        CartComponent: {
          component: EbCartDetailsComponent,
          guards: [EbAuthGuard]
        },
        CartTotalsComponent: {
          component: EbCartTotalsComponent
        }
      }
    } as CmsConfig),
    {
      provide: OccCartAdapter,
      useClass: EbCartAdapter
    },
    {
      provide: MultiCartFacade,
      useClass: EbMultiCartService
    },
    {
      provide: ActiveCartFacade,
      useClass: EbActiveCartService
    },
    {
      provide: CartPageLayoutHandler,
      useClass: EbCartPageLayoutHandler
    },
    {
      provide: APP_INITIALIZER,
      useFactory: EbMultiCartService.keepActiveCartIdInSync,
      multi: true,
      deps: [Store, ConfigurationService, WindowRef]
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: EbCartInterceptor,
      multi: true
    },
    EbQuickOrderService
  ],
  exports: [
    EbAddToCartComponent,
    EbAddedToCartDialogComponent,
    EbCartItemComponent,
    EbCartItemHeaderComponent,
    EbCartDetailsComponent,
    EbCartItemListComponent,
    EbOrderSummaryComponent,
    EbCartTotalsComponent,
    EbPurchaseProductComponent,
    EbQuickOrderFormComponent,
    EbProductSalesTextComponent
  ]
})
export class EbCartModule {}
